import { Navigate, Route, Routes } from "react-router-dom";

import { Layout } from "@/components/Layout";
import { ToastProvider } from "@/components/Toast";
import { RequireAdmin, RequirePermission, RequireAuth } from "@/auth/RequireAuth";

import { FeaturesPage } from "@/pages/Features";
import { LoginPage } from "@/pages/Login";
import { RegisterPage } from "@/pages/Register";
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { ResetPasswordPage } from "@/pages/ResetPassword";
import { VerifyEmailPage } from "@/pages/VerifyEmail";
import { ProfilePage } from "@/pages/Profile";
import { ApiKeysPage } from "@/pages/ApiKeys";
import { DiscoverPage } from "@/pages/Discover";
import { AcademyPage } from "@/pages/Academy";
import { TearsheetPage } from "@/pages/Tearsheet";
import { TerminalPage } from "@/pages/terminal/Terminal";
import { PortfolioPage } from "@/pages/Portfolio";
import { OrdersPage } from "@/pages/Orders";
import { FillsPage } from "@/pages/Fills";
import { AlertsPage } from "@/pages/Alerts";
import { PositionHistoryPage } from "@/pages/PositionHistory";
import { CommissionsPage } from "@/pages/Commissions";
import { StatementsPage } from "@/pages/Statements";
import { AppropriatenessPage } from "@/pages/Appropriateness";
import { ComplaintsPage } from "@/pages/Complaints";
import { AdminComplaintsPage } from "@/pages/AdminComplaints";
import { WithdrawalsPage } from "@/pages/Withdrawals";
import { AdminWithdrawalsPage } from "@/pages/AdminWithdrawals";
import { AdminDepositsPage } from "@/pages/AdminDeposits";
import { AdminTreasuryPage } from "@/pages/AdminTreasury";
import { AdminMarginCallsPage } from "@/pages/AdminMarginCalls";
import { AdminIbPage } from "@/pages/AdminIb";
import { AdminShopPage } from "@/pages/AdminShop";
import { AdminPromoCodesPage } from "@/pages/AdminPromoCodes";
import { AdminMarketingPage } from "@/pages/AdminMarketing";
import { AdminTcaPage } from "@/pages/AdminTca";
import { AdminBiPage } from "@/pages/AdminBi";
import { AdminGlPage } from "@/pages/AdminGl";
import { AdminReconciliationPage } from "@/pages/AdminReconciliation";
import { AdminCorporateActionsPage } from "@/pages/AdminCorporateActions";
import { AdminFundingRatesPage } from "@/pages/AdminFundingRates";
import { AdminFeatureFlagsPage } from "@/pages/AdminFeatureFlags";
import { AdminWebhooksPage } from "@/pages/AdminWebhooks";
import { AdminCopyTradingPage } from "@/pages/AdminCopyTrading";
import { AdminStatusPage } from "@/pages/AdminStatus";
import { AdminPushPage } from "@/pages/AdminPush";
import { AdminPendingChangesPage } from "@/pages/AdminPendingChanges";
import { AdminTaxDocsPage } from "@/pages/AdminTaxDocs";
import { StrategiesPage } from "@/pages/Strategies";
import { RobotsPage } from "@/pages/Robots";
import { AnalyticsPage } from "@/pages/Analytics";
import { InsightsPage } from "@/pages/Insights";
import { BrokerAccountsPage } from "@/pages/BrokerAccounts";
import { AdminCustomDomainsPage } from "@/pages/AdminCustomDomains";
import { AdminUsagePage } from "@/pages/AdminUsage";
import { AdminBrandingPage } from "@/pages/AdminBranding";
import { AdminAuditPage } from "@/pages/AdminAudit";
import { AdminUsersPage } from "@/pages/AdminUsers";
import { AdminRolesPage } from "@/pages/AdminRoles";
import { AdminSanctionsPage } from "@/pages/AdminSanctions";
import { AdminAmlAlertsPage } from "@/pages/AdminAmlAlerts";
import { AdminSurveillancePage } from "@/pages/AdminSurveillance";
import { AdminBestExecutionPage } from "@/pages/AdminBestExecution";
import { AdminTransactionReportsPage } from "@/pages/AdminTransactionReports";
import { AdminOidcPage } from "@/pages/AdminOidc";
import { AdminKycPage } from "@/pages/AdminKyc";
import { TradingAccountsPage } from "@/pages/TradingAccounts";
import { AdminAccountGroupsPage } from "@/pages/AdminAccountGroups";
import { DealingDeskPage } from "@/pages/DealingDesk";
import { AdminLpSessionsPage } from "@/pages/AdminLpSessions";
import { AdminRiskLimitsPage } from "@/pages/AdminRiskLimits";
import { AdminOrganizationsPage } from "@/pages/AdminOrganizations";
import { AdminDashboardPage } from "@/pages/AdminDashboard";

export function App() {
  return (
    <ToastProvider>
      <Routes>
        {/* Public auth shell */}
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Authenticated app */}
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/terminal" replace />} />
          <Route path="/terminal" element={<TerminalPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/fills" element={<FillsPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/position-history" element={<PositionHistoryPage />} />
          <Route path="/commissions" element={<CommissionsPage />} />
          <Route path="/statements" element={<StatementsPage />} />
          <Route path="/appropriateness" element={<AppropriatenessPage />} />
          <Route path="/complaints" element={<ComplaintsPage />} />
          <Route path="/admin/complaints" element={<RequireAdmin><AdminComplaintsPage /></RequireAdmin>} />
          <Route path="/withdrawals" element={<WithdrawalsPage />} />
          <Route path="/admin/withdrawals" element={<RequireAdmin><AdminWithdrawalsPage /></RequireAdmin>} />
          <Route path="/admin/deposits" element={<RequireAdmin><AdminDepositsPage /></RequireAdmin>} />
          <Route path="/admin/treasury" element={<RequireAdmin><AdminTreasuryPage /></RequireAdmin>} />
          <Route path="/admin/margin-calls" element={<RequireAdmin><AdminMarginCallsPage /></RequireAdmin>} />
          <Route path="/admin/ib" element={<RequireAdmin><AdminIbPage /></RequireAdmin>} />
          <Route path="/admin/shop" element={<RequirePermission anyOf={["shop:manage", "admin:full"]}><AdminShopPage /></RequirePermission>} />
          <Route path="/admin/promo-codes" element={<RequireAdmin><AdminPromoCodesPage /></RequireAdmin>} />
          <Route path="/admin/marketing" element={<RequireAdmin><AdminMarketingPage /></RequireAdmin>} />
          <Route path="/admin/tca" element={<RequireAdmin><AdminTcaPage /></RequireAdmin>} />
          <Route path="/admin/bi" element={<RequireAdmin><AdminBiPage /></RequireAdmin>} />
          <Route path="/admin/gl" element={<RequireAdmin><AdminGlPage /></RequireAdmin>} />
          <Route path="/admin/reconciliation" element={<RequireAdmin><AdminReconciliationPage /></RequireAdmin>} />
          <Route path="/admin/corporate-actions" element={<RequireAdmin><AdminCorporateActionsPage /></RequireAdmin>} />
          <Route path="/admin/funding-rates" element={<RequireAdmin><AdminFundingRatesPage /></RequireAdmin>} />
          <Route path="/admin/feature-flags" element={<RequireAdmin><AdminFeatureFlagsPage /></RequireAdmin>} />
          <Route path="/admin/webhooks" element={<RequireAdmin><AdminWebhooksPage /></RequireAdmin>} />
          <Route path="/admin/copy-trading" element={<RequireAdmin><AdminCopyTradingPage /></RequireAdmin>} />
          <Route path="/admin/status" element={<RequireAdmin><AdminStatusPage /></RequireAdmin>} />
          <Route path="/admin/push" element={<RequireAdmin><AdminPushPage /></RequireAdmin>} />
          <Route path="/admin/pending-changes" element={<RequireAdmin><AdminPendingChangesPage /></RequireAdmin>} />
          <Route path="/admin/tax-documents" element={<RequireAdmin><AdminTaxDocsPage /></RequireAdmin>} />
          <Route path="/strategies" element={<StrategiesPage />} />
          <Route path="/robots" element={<RobotsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/broker-accounts" element={<BrokerAccountsPage />} />
          <Route path="/trading-accounts" element={<TradingAccountsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/api-keys" element={<ApiKeysPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/academy" element={<AcademyPage />} />
          <Route path="/tearsheet" element={<TearsheetPage />} />

          <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboardPage /></RequireAdmin>} />
          <Route path="/admin/users" element={<RequirePermission anyOf={["users:manage", "admin:full"]}><AdminUsersPage /></RequirePermission>} />
          <Route path="/admin/roles" element={<RequirePermission anyOf={["users:manage", "admin:full"]}><AdminRolesPage /></RequirePermission>} />
          <Route path="/admin/sanctions" element={<RequireAdmin><AdminSanctionsPage /></RequireAdmin>} />
          <Route path="/admin/aml" element={<RequireAdmin><AdminAmlAlertsPage /></RequireAdmin>} />
          <Route path="/admin/surveillance" element={<RequireAdmin><AdminSurveillancePage /></RequireAdmin>} />
          <Route path="/admin/best-execution" element={<RequireAdmin><AdminBestExecutionPage /></RequireAdmin>} />
          <Route path="/admin/rts22" element={<RequireAdmin><AdminTransactionReportsPage /></RequireAdmin>} />
          <Route path="/admin/account-groups" element={<RequireAdmin><AdminAccountGroupsPage /></RequireAdmin>} />
          <Route path="/admin/dealing-desk" element={<RequirePermission anyOf={["dealing_desk:read", "dealing_desk:approve", "admin:full"]}><DealingDeskPage /></RequirePermission>} />
          <Route path="/admin/lp-sessions" element={<RequirePermission anyOf={["brokers:manage", "admin:full"]}><AdminLpSessionsPage /></RequirePermission>} />
          <Route path="/admin/risk-limits" element={<RequireAdmin><AdminRiskLimitsPage /></RequireAdmin>} />
          <Route path="/admin/organizations" element={<RequireAdmin><AdminOrganizationsPage /></RequireAdmin>} />
          <Route path="/admin/audit" element={<RequirePermission anyOf={["audit:read", "admin:full"]}><AdminAuditPage /></RequirePermission>} />
          <Route path="/admin/usage" element={<RequireAdmin><AdminUsagePage /></RequireAdmin>} />
          <Route path="/admin/custom-domains" element={<RequireAdmin><AdminCustomDomainsPage /></RequireAdmin>} />
          <Route path="/admin/oidc" element={<RequireAdmin><AdminOidcPage /></RequireAdmin>} />
          <Route path="/admin/kyc" element={<RequireAdmin><AdminKycPage /></RequireAdmin>} />
          <Route path="/admin/branding" element={<RequireAdmin><AdminBrandingPage /></RequireAdmin>} />
        </Route>

        {/* Anything else: bounce. RequireAuth/RequireAdmin handle the gate above. */}
        <Route path="*" element={<Navigate to="/terminal" replace />} />
      </Routes>
    </ToastProvider>
  );
}
